import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { performanceAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, Loader2, Eye } from 'lucide-react';
import ModuleDisabled from '../../components/shared/ModuleDisabled';

const Performance = () => {
  const { user } = useAuth();
  const [cycles, setCycles] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    self_review_start: '',
    self_review_end: '',
    manager_review_start: '',
    manager_review_end: '',
    status: 'DRAFT',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [cyclesRes, reviewsRes, pendingRes] = await Promise.all([
        performanceAPI.getCycles(),
        performanceAPI.getReviews(),
        performanceAPI.getPendingManagerReviews(),
      ]);
      setCycles(cyclesRes.data);
      setReviews(reviewsRes.data);
      setPendingReviews(pendingRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setError(error.response.data.detail);
      } else {
        toast.error('Failed to fetch performance data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCycle = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      await performanceAPI.createCycle({
        ...formData,
        company_id: user.company_id,
      });
      toast.success('Performance cycle created');
      setIsCreateOpen(false);
      setFormData({
        name: '',
        start_date: '',
        end_date: '',
        self_review_start: '',
        self_review_end: '',
        manager_review_start: '',
        manager_review_end: '',
        status: 'DRAFT',
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create performance cycle');
    } finally {
      setCreateLoading(false);
    }
  };

  const statusColors = {
    DRAFT: 'bg-gray-100 text-gray-800',
    ACTIVE: 'bg-green-100 text-green-800',
    COMPLETED: 'bg-blue-100 text-blue-800',
    CANCELLED: 'bg-red-100 text-red-800',
    PENDING: 'bg-yellow-100 text-yellow-800',
    SELF_REVIEW_DONE: 'bg-blue-100 text-blue-800',
    MANAGER_REVIEW_DONE: 'bg-purple-100 text-purple-800',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return <ModuleDisabled moduleName="performance" />;
  }

  return (
    <div className="space-y-6 animate-slide-up">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Performance Management</h1>
          <p className="text-muted-foreground text-sm sm:text-base">Manage performance cycles and reviews</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-cycle-btn">
              <Plus className="mr-2 h-4 w-4" />
              New Cycle
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Performance Cycle</DialogTitle>
              <DialogDescription>
                Define a new performance review cycle
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateCycle}>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Cycle Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Q1 2024 Review"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(v) => setFormData({ ...formData, status: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DRAFT">Draft</SelectItem>
                        <SelectItem value="ACTIVE">Active</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Cycle Start Date *</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cycle End Date *</Label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Self Review Start</Label>
                    <Input
                      type="date"
                      value={formData.self_review_start}
                      onChange={(e) => setFormData({ ...formData, self_review_start: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Self Review End</Label>
                    <Input
                      type="date"
                      value={formData.self_review_end}
                      onChange={(e) => setFormData({ ...formData, self_review_end: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Manager Review Start</Label>
                    <Input
                      type="date"
                      value={formData.manager_review_start}
                      onChange={(e) => setFormData({ ...formData, manager_review_start: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Manager Review End</Label>
                    <Input
                      type="date"
                      value={formData.manager_review_end}
                      onChange={(e) => setFormData({ ...formData, manager_review_end: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createLoading}>
                  {createLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Create
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="cycles" className="space-y-6">
        <TabsList className="w-full sm:w-auto flex-wrap h-auto gap-1">
          <TabsTrigger value="cycles">Cycles</TabsTrigger>
          <TabsTrigger value="reviews">All Reviews</TabsTrigger>
          <TabsTrigger value="pending">
            Pending Reviews
            {pendingReviews.length > 0 && (
              <Badge variant="destructive" className="ml-2">{pendingReviews.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cycles">
          <Card>
            <CardHeader>
              <CardTitle>Performance Cycles</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cycle Name</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Self Review</TableHead>
                    <TableHead>Manager Review</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cycles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                        No performance cycles found
                      </TableCell>
                    </TableRow>
                  ) : (
                    cycles.map((cycle) => (
                      <TableRow key={cycle.id}>
                        <TableCell className="font-medium">{cycle.name}</TableCell>
                        <TableCell>
                          {new Date(cycle.start_date).toLocaleDateString()} - {new Date(cycle.end_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {cycle.self_review_start ? (
                            <>
                              {new Date(cycle.self_review_start).toLocaleDateString()} - {new Date(cycle.self_review_end).toLocaleDateString()}
                            </>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {cycle.manager_review_start ? (
                            <>
                              {new Date(cycle.manager_review_start).toLocaleDateString()} - {new Date(cycle.manager_review_end).toLocaleDateString()}
                            </>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={statusColors[cycle.status]}>{cycle.status}</Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reviews">
          <Card>
            <CardHeader>
              <CardTitle>All Reviews</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Cycle</TableHead>
                    <TableHead>Self Rating</TableHead>
                    <TableHead>Manager Rating</TableHead>
                    <TableHead>Final Rating</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reviews.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                        No reviews found
                      </TableCell>
                    </TableRow>
                  ) : (
                    reviews.map((review) => (
                      <TableRow key={review.id}>
                        <TableCell className="font-medium">{review.employee_name}</TableCell>
                        <TableCell>{review.cycle_name}</TableCell>
                        <TableCell>{review.self_rating?.replace(/_/g, ' ') || '-'}</TableCell>
                        <TableCell>{review.manager_rating?.replace(/_/g, ' ') || '-'}</TableCell>
                        <TableCell>{review.final_rating?.replace(/_/g, ' ') || '-'}</TableCell>
                        <TableCell>
                          <Badge className={statusColors[review.status]}>{review.status.replace(/_/g, ' ')}</Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Pending Manager Reviews</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Cycle</TableHead>
                    <TableHead>Self Rating</TableHead>
                    <TableHead>Self Comments</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingReviews.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                        No pending reviews
                      </TableCell>
                    </TableRow>
                  ) : (
                    pendingReviews.map((review) => (
                      <TableRow key={review.id}>
                        <TableCell className="font-medium">{review.employee_name}</TableCell>
                        <TableCell>{review.cycle_name}</TableCell>
                        <TableCell>{review.self_rating?.replace(/_/g, ' ') || '-'}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{review.self_comments || '-'}</TableCell>
                        <TableCell>
                          <Badge className={statusColors[review.status]}>{review.status.replace(/_/g, ' ')}</Badge>
                        </TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm">
                            <Eye className="h-4 w-4 mr-1" />
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Performance;
